import type { MyInvitation } from "@minga/protocol"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { type FormEvent, useState } from "react"
import { Logo } from "@/components/logo"
import { ErrorText } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardDescription, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { VERIFY_CALLBACK_URL, verifiedSearch } from "@/components/verify-banner"
import { WorkspaceTile } from "@/components/workspace-tile"
import {
  maskEmail,
  newWorkspaceSlug,
  ROLE_LABEL,
  slugify,
  workspaceProblems,
} from "@/features/workspaces/workspace-model"
import { authClient, unwrap } from "@/lib/auth-client"
import { sessionQuery, workspacesQuery } from "@/lib/queries"
import { useSignOut } from "@/lib/session"
import { invitationLanding, myInvitationsQuery } from "@/lib/settings-api"
import { formatRelative } from "@/lib/time"
import { WORDS } from "@/model/glossary"
import { joinTarget } from "./invite"

/**
 * Decision 0166 §5: the confirmation after the verify link: shown once the address really is verified (a link that
 * failed lands without it being verified, and then says nothing).
 */
export const verifiedNotice = (
  search: { readonly verified?: "1" },
  user: { readonly email: string; readonly emailVerified?: boolean | null } | null | undefined,
): string | null =>
  search.verified === "1" && user?.emailVerified === true
    ? `Verified. ${maskEmail(user.email)} is confirmed, so invitations sent to it show here.`
    : null

/** `/welcome`: people with no workspace yet (earlier spec §5.3, UX §4): pending invitations, then "create one". */
export function WelcomePage() {
  const session = useQuery(sessionQuery).data
  const invitations = useQuery(myInvitationsQuery)
  const signOut = useSignOut()
  const verified = verifiedNotice(verifiedSearch(useSearch({ strict: false })), session?.user)
  const first = session?.user.name?.trim().split(/\s+/)[0]
  const list = invitations.data ?? []
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-6 px-4 py-10">
      <Logo />
      <div className="text-center">
        <h1 className="type-page">Welcome to Minga{first ? `, ${first}` : ""}.</h1>
        <p className="mt-1 text-muted">
          A workspace holds your projects, agents and runners. You can invite people to it later.
        </p>
      </div>
      {verified && (
        <p
          data-verified=""
          role="status"
          className="w-full max-w-md rounded-card border border-line bg-surface px-4 py-3 text-sm text-ink"
        >
          {verified}
        </p>
      )}
      {/* decision 0130 (audit item 11): invitations to an unverified address stay hidden (sign-up is open), so say
          how to see them instead of only offering a workspace of their own */}
      {session?.user.emailVerified === false && <VerifyForInvitations email={session.user.email} />}
      {list.map((inv) => (
        <InvitationCard key={inv.id} invitation={inv} />
      ))}
      {list.length > 0 && <p className="text-sm text-muted">or start your own workspace</p>}
      <CreateFirstWorkspace />
      <p className="text-sm text-muted">
        Signed in as {maskEmail(session?.user.email)} ·{" "}
        <button type="button" className="font-semibold link" onClick={() => void signOut()}>
          Sign out
        </button>
      </p>
    </main>
  )
}

/** Unverified: "Invited? Verify your address, then your invitation appears here", and Send the link again. */
export function VerifyForInvitations({ email }: { email: string }) {
  const send = useMutation({
    mutationFn: async () =>
      unwrap(await authClient.sendVerificationEmail({ email, callbackURL: VERIFY_CALLBACK_URL })),
  })
  return (
    <Card className="w-full max-w-md" data-verify-invitations="">
      <CardTitle>Were you invited?</CardTitle>
      <CardDescription>
        Check your email and verify {maskEmail(email)}, then your invitation appears here. Or open the link in
        the invitation email.
      </CardDescription>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {send.isSuccess ? (
          <span className="text-sm text-muted">Sent. Check your inbox.</span>
        ) : (
          <Button size="md" variant="secondary" disabled={send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Sending…" : "Send the verification email again"}
          </Button>
        )}
        {send.isError && (
          <span className="text-sm text-blocked">That didn't work. Try again in a minute.</span>
        )}
      </div>
    </Card>
  )
}

function InvitationCard({ invitation }: { invitation: MyInvitation }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: workspacesQuery.queryKey }),
      queryClient.invalidateQueries({ queryKey: myInvitationsQuery.queryKey }),
    ])
  const join = useMutation({
    mutationFn: async () =>
      unwrap(await authClient.organization.acceptInvitation({ invitationId: invitation.id })),
    onSuccess: async (data) => {
      await refresh()
      // decision 0130: open the project the invitation named, not workspace Home
      await navigate(
        joinTarget(
          data?.member.organizationId ?? invitation.workspace.id,
          await invitationLanding(invitation.id),
        ),
      )
    },
  })
  const decline = useMutation({
    mutationFn: async () =>
      unwrap(await authClient.organization.rejectInvitation({ invitationId: invitation.id })),
    onSuccess: refresh,
  })
  return (
    <Card className="w-full max-w-md">
      <div className="mono-data text-muted">YOU'RE INVITED</div>
      <div className="mt-2 flex items-center gap-2.5">
        <WorkspaceTile name={invitation.workspace.name} size={32} />
        <CardTitle>{invitation.workspace.name}</CardTitle>
      </div>
      <CardDescription>
        {invitation.inviterName} invited you as {ROLE_LABEL[invitation.role]} ·{" "}
        {formatRelative(new Date(invitation.createdAt))}
      </CardDescription>
      {(join.error || decline.error) && (
        <ErrorText className="mt-2">{(join.error ?? decline.error)?.message}</ErrorText>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button
          variant="secondary"
          size="md"
          disabled={join.isPending || decline.isPending}
          onClick={() => decline.mutate()}
        >
          Decline
        </Button>
        <Button size="md" disabled={join.isPending || decline.isPending} onClick={() => join.mutate()}>
          {join.isPending ? "Joining…" : "Join"}
        </Button>
      </div>
    </Card>
  )
}

function CreateFirstWorkspace() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState("")
  /** null: derived from the name (with a random suffix at create time). */
  const [slug, setSlug] = useState<string | null>(null)
  const [taken, setTaken] = useState(false)
  const problems = workspaceProblems({ name, slug: slug ?? "auto" })
  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await authClient.organization.create({ name: name.trim(), slug: slug ?? newWorkspaceSlug(name) }),
      ),
    onSuccess: async (org) => {
      await queryClient.invalidateQueries({ queryKey: workspacesQuery.queryKey })
      if (org) await navigate({ to: "/w/$workspaceId", params: { workspaceId: org.id } })
    },
  })
  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (problems.name || problems.slug || taken) return
    create.mutate()
  }
  const checkSlug = async () => {
    if (slug === null || problems.slug) return
    try {
      const result = await authClient.organization.checkSlug({ slug })
      setTaken(Boolean(result.error))
    } catch {
      // the server validates again
    }
  }
  return (
    <Card className="w-full max-w-md">
      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        <label htmlFor="welcome-name" className="text-sm font-medium">
          Workspace name
        </label>
        <Input
          id="welcome-name"
          value={name}
          maxLength={48}
          placeholder="Acme"
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex items-center gap-2 text-xs text-muted">
          <label htmlFor="welcome-slug">{WORDS.address}</label>
          <Input
            id="welcome-slug"
            value={slug ?? ""}
            placeholder={name.trim() ? `${slugify(name)}-…` : "from the name"}
            onChange={(e) => {
              setSlug(e.target.value ? e.target.value.toLowerCase() : null)
              setTaken(false)
            }}
            onBlur={checkSlug}
            className="h-8 font-mono text-xs"
          />
        </div>
        {slug !== null && problems.slug && <p className="text-xs text-blocked">{problems.slug}</p>}
        {taken && <p className="text-xs text-blocked">That address is taken. Try another one.</p>}
        {create.error && <ErrorText>{create.error.message}</ErrorText>}
        <Button type="submit" disabled={!name.trim() || create.isPending}>
          {create.isPending ? "Creating…" : "Create workspace"}
        </Button>
      </form>
    </Card>
  )
}
